import type { NativeImageProps } from '@Pages/pageRuntime/hooks/useFileImageDelivery';
import { cn } from '@Pages/pageRuntime/lib/utils';
import ImageOff from 'lucide-react/dist/esm/icons/image-off';
import React from 'react';

import { useFileImage } from './useFileImage';

type ImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'alt' | 'src'> & {
  ref?: React.Ref<HTMLImageElement>;

  /**
   * Image URL. Pass `Sdk.utils.file(record.file_field)` directly; null shows the fallback.
   */
  src?: string | null;

  /**
   * Accessible image description. Use an empty string only when the image is decorative.
   */
  alt: string;
};

/**
 * The runtime's unstyled native image for a workspace file source. The page
 * runtime renders a plain `<img>` with a `/files/{uuid}` source through this
 * component. It adds no wrapper, class, or fallback.
 */
export function FileImage(props: NativeImageProps) {
  const imageProps = useFileImage(props);

  return <img {...imageProps} />;
}

/**
 * A responsive image with a designed fallback for missing or broken sources. Native image
 * attributes, event handlers, and refs are forwarded.
 *
 * @remarks
 * A workspace file source from `Sdk.utils.file(id)` loads a suitable size automatically.
 * When the page has authorized metadata for the file, the image reserves the file's
 * aspect ratio, and the browser selects from the file's available widths with `sizes`
 * set to the rendered width. Unspecified `loading` is eager for an image in the initial
 * viewport and lazy otherwise. If a resized request fails, the image retries the original
 * once. A private file, or a file without metadata, loads the original. An explicit
 * `srcSet`, a parent `<picture>`, or `Sdk.utils.file(id, { width })` keeps the authored
 * selection. Other URLs are used as given.
 * Pass `Sdk.utils.file(id, { quality: 90 })` to override the default quality of 85
 * while keeping automatic sizing. Quality applies only to transformed public images.
 *
 * File metadata supplies missing `width` and `height` attributes. When CSS controls one
 * dimension, set the other to `auto` to preserve the ratio: use `w-full h-auto` for
 * width-based sizing or `h-24 w-auto` for height-based sizing. For inline styles, use
 * `style={{ height: 96, width: 'auto' }}`. Set both dimensions intentionally for a fixed
 * crop and use `object-cover`.
 *
 * @example
 * <Sdk.Components.Image
 *   src={Sdk.utils.file(student.avatar)}
 *   alt={student.name ?? 'Student portrait'}
 *   className="aspect-video object-cover"
 * />
 */
export function Image({
  src,
  alt,
  className,
  onError,
  onLoad,
  style,
  width,
  height,
  ...props
}: ImageProps) {
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const hasSource = typeof src === 'string' && src !== '';
  const showFallback = !hasSource || failedSrc === src;
  const imageProps = useFileImage(
    {
      ...props,
      src: hasSource ? src : undefined,
      alt,
      className: cn('max-w-full rounded-lg border border-border', className),
      style,
      width,
      height,
      onError,
      onLoad: (event) => {
        setFailedSrc(null);
        onLoad?.(event);
      }
    },
    {
      defaultLoading: 'lazy',
      onTerminalError: () => setFailedSrc(hasSource ? src : null)
    }
  );

  if (showFallback) {
    return (
      <div
        role={alt ? 'img' : undefined}
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : true}
        className={cn(
          'flex aspect-video w-full max-w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-sm text-muted-foreground',
          className
        )}
        style={{ width, height, ...style }}
      >
        <ImageOff aria-hidden="true" className="size-5 shrink-0" />
        <span aria-hidden="true">Image unavailable</span>
      </div>
    );
  }

  return <img {...imageProps} />;
}
